import React, { useRef } from 'react';
import {
    Links,
    Meta,
    Outlet,
    Scripts,
    isRouteErrorResponse,
    useLocation,
    useRouteError,
    useRouteLoaderData,
} from 'react-router';
import { useSetAtom } from 'jotai';
import type { LinksFunction } from 'react-router';
import type { GrowthBookSSRData } from '@growthbook/growthbook-react';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { useHydrated } from 'remix-utils';
import {
    getThemeByUrl,
    processTheme,
    useMatchesHandleData,
    useRootData,
} from '@modules/root';
import {
    DeviceBrowserContextProvider,
    DeviceTypeContextProvider,
    AppDisplayModeContextProvider,
    Provider,
    baseTheme,
    RendererProvider,
} from '@archipro-design/aria';
import { BodyPixels, HeadPixels } from '@modules/tracking';
import { ClientAdvertisingPixels } from '@modules/tracking/component/ClientAdvertisingPixels';
import { UsNoticeBanner } from '@modules/consent/UsNoticeBanner';
import ErrorPage from '../../error/page/ErrorPage';
import type { ReactNode, JSX } from 'react';
import { ConfigProvider } from '@archipro-website/config/bindings/react';
import { CountryProvider } from '@archipro-website/localisation';
import CustomScrollRestoration from '@modules/root/component/scroll-restoration/CustomScrollRestoration';
import { LocalisationContextProvider } from '@archipro-website/localisation';
import { useMockDevice } from '@modules/root/hook/use-mock-device';
import { DevicePreview } from '@modules/root/component/device-preview/DevicePreview';
import {
    TrackerContext,
    isHubspotStaffUser,
    useArchiproTracking,
} from '@archipro-website/tracker';
import { useGrowthBookClient } from '../hook/use-growthbook';
import Debug from '~/modules/error/component/debug/Debug';
import { createFelaRendererFactory } from '../../../../react-northstar-fela-renderer/src/createFelaRenderer';
import { useFluentContext } from '@fluentui/react-northstar';
import { _displayModeAtom } from '@rocco/states/uiContextAtomDisplayMode';
import { getSiteLang } from '~/utils/countryHelper';
import type { RootData } from '~/root';
import { ApolloClientProvider } from '../context/ApolloClientProvider';
import { useUser } from '@modules/user';

/**
 * Site wide fonts and favicons.
 */
export const links: LinksFunction = () => {
    return [
        // Preconnect hints for TypeKit fonts to reduce latency
        // Note: crossOrigin removed for stylesheet preconnect (CSS doesn't use CORS)
        {
            rel: 'preconnect',
            href: 'https://use.typekit.net',
        },
        {
            rel: 'preconnect',
            href: 'https://p.typekit.net',
            crossOrigin: 'anonymous',
        },
        {
            rel: 'dns-prefetch',
            href: 'https://use.typekit.net',
        },
        // Adobe Fonts kit supplying neue-haas-grotesk-display / -text. Declared here
        // so it renders into <head> and the preload scanner starts it on the first
        // pass. The font binaries sit behind rotating hashed p.typekit.net URLs and
        // cannot be preloaded, so an early kit request is the only latency lever.
        {
            rel: 'stylesheet',
            href: 'https://use.typekit.net/hpu6ldu.css',
        },
        // Commenting this out again, its still causing mobile performance
        // issues on mobile. Should fall back to pngs below.
        // {
        //     rel: 'icon',
        //     type: 'image/svg+xml',
        //     href: '/assets/website/ui/favicon-global-ninjas/favicon-new.svg',
        // },
        {
            rel: 'icon',
            type: 'image/png',
            sizes: '196x196',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-196x196.png',
        },
        {
            rel: 'icon',
            type: 'image/png',
            sizes: '16x16',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-16x16.png',
        },
        {
            rel: 'icon',
            type: 'image/png',
            sizes: '32x32',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-32x32.png',
        },
        {
            rel: 'icon',
            type: 'image/png',
            sizes: '96x96',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-96x96.png',
        },
        {
            rel: 'icon',
            type: 'image/png',
            sizes: '128x128',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-128x128.png',
        },
        {
            rel: 'icon',
            type: 'image/png',
            sizes: '512x512',
            href: '/assets/website/ui/favicon-global-ninjas/android-chrome-black-512x512.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '57x57',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-57x57.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '114x114',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-114x114.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '60x60',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-60x60.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '72x72',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-72x72.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '76x76',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-76x76.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '120x120',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-120x120.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '152x152',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-152x152.png',
        },
        {
            rel: 'apple-touch-icon-precomposed',
            sizes: '144x144',
            href: '/assets/website/ui/favicon-global-ninjas/favicon-black-144x144.png',
        },
    ];
};

/**
 * Full body of the app rendered on the server and the browser.
 */
const Body = ({ children }: { children: React.ReactNode }) => {
    const {
        device,
        localisation,
        config,
        appDisplayMode,
        growthbookContext,
        env,
    } = useRootData();
    // Prefer `_app` user so SPA login that revalidates `_app` (before full reload)
    // can rebuild HubSpot trackers with staff `doNotTrack`.
    const user = useUser();

    const location = useLocation();
    const hydrated = useHydrated();
    const mockDevice = useMockDevice(location.search);

    // set the display mode for using in rocco UI components
    const setDisplayMode = useSetAtom(_displayModeAtom);
    // Honours ?deviceView the same way the aria provider below does. Rocco
    // components that switch surface on this atom — the auth gate sheet, the
    // moodboard drawer — were otherwise unreachable from a desktop browser.
    //
    // In an effect rather than in render: the mode now answers to how much room
    // the reader has, so it changes when they zoom, and writing an atom other
    // components read during a render is what React reports as updating one
    // component while rendering another.
    const displayMode = mockDevice ?? appDisplayMode;
    React.useEffect(() => {
        setDisplayMode(displayMode);
    }, [setDisplayMode, displayMode]);

    if (mockDevice) {
        device.type = mockDevice;
    }

    const theme = processTheme(
        getThemeByUrl(location.pathname),
        device.type === 'desktop',
    );

    // Sets up all of our different tracking pixels
    const tracker = useArchiproTracking(location, user, {
        ga4TrackingId: config.pixels.ga4TrackingId,
        googleAdsConversionLabels: config.pixels.googleAdsConversionLabels,
    });

    const gb = useGrowthBookClient(
        growthbookContext as GrowthBookSSRData,
        tracker,
    );

    // only load the dynamic favicon script in development environment
    const isDevEnv =
        env.stage !== 'Production' ||
        env.host === 'localhost' ||
        env.host.includes('.team.archipro.');
    const dynamicFaviconScript = isDevEnv ? (
        <script
            src={`/_resources/app/js/dynamic-favicon.js?VERSION=${env.version}&date=${new Date().toISOString().split('T')[0]}`}
        />
    ) : null;

    return (
        <div
            id="root"
            style={{
                backgroundColor: theme.siteVariables.bodyBackground,
            }}
        >
            {mockDevice === undefined && <BodyPixels {...config.pixels} />}

            <TrackerContext.Provider value={tracker}>
                <GrowthBookProvider growthbook={gb}>
                    <Provider theme={theme}>
                        {import.meta.env.DEV ? <Debug /> : null}
                        <LocalisationContextProvider
                            data={localisation}
                            hydrated={hydrated}
                        >
                            <DeviceTypeContextProvider device={device.type}>
                                <DeviceBrowserContextProvider
                                    browser={device.browser}
                                >
                                    <AppDisplayModeContextProvider
                                        mode={mockDevice ?? appDisplayMode}
                                    >
                                        <CountryProvider
                                            code={localisation.country}
                                        >
                                            <ConfigProvider config={config}>
                                                <ApolloClientProvider>
                                                    <ClientAdvertisingPixels
                                                        {...config.pixels}
                                                        enabled={
                                                            mockDevice ===
                                                            undefined
                                                        }
                                                    />
                                                    <UsNoticeBanner />
                                                    {mockDevice ? (
                                                        <DevicePreview>
                                                            {children}
                                                        </DevicePreview>
                                                    ) : (
                                                        <>{children}</>
                                                    )}
                                                </ApolloClientProvider>
                                            </ConfigProvider>
                                        </CountryProvider>
                                    </AppDisplayModeContextProvider>
                                </DeviceBrowserContextProvider>
                            </DeviceTypeContextProvider>
                        </LocalisationContextProvider>
                    </Provider>
                </GrowthBookProvider>
            </TrackerContext.Provider>
            <CustomScrollRestoration />
            <Scripts />
            {dynamicFaviconScript}
        </div>
    );
};

/**
 * Publishes how much larger the reader wants the page as `--ap-user-scale`,
 * the multiplier every rule that sets a root font-size reads.
 *
 * Two independent preferences feed it, and the rem scale is blind to both:
 *
 * - The browser's default font size. A probe styled `font-size: medium`
 *   resolves to exactly that default; the ratio against the design's 16px base
 *   is the first factor.
 * - Page zoom. `outerWidth` is measured in the window's own pixels rather than
 *   the page's, so the ratio between the two is the zoom level — the one signal
 *   CSS cannot see, because every CSS length is either a fixed multiple of the
 *   CSS pixel or a fraction of a viewport.
 *
 * `--ap-user-scale` is a correction, not the magnification the reader asked
 * for, because browsers disagree about `vw` under zoom. Blink shrinks the
 * viewport `vw` resolves against, so a `vw`-derived root cancels zoom out
 * exactly and Ctrl+ moves nothing; WebKit keeps `vw` on the unzoomed width, so
 * zoom already lands and multiplying by it again counts it twice — at 125% that
 * is a root a quarter too large and a design drawn for 120rem rendering into
 * 76. Rather than branch on the engine, a probe styled with the same
 * `min(16px, 0.833vw)` the rules use reports what this browser resolves it to
 * right now, and the published factor is whatever turns that into the size the
 * design wants: `min(16px, 0.833% of the unzoomed width)`, times the font-size
 * preference. Both engines land on the same root, and an engine that resolves
 * `vw` some third way needs no new case.
 *
 * The hold reads the magnification itself — preference times zoom — because
 * what it asks is how much the reader has grown the page, not how far this
 * engine's `vw` had to be corrected.
 *
 * The ratio carries a fixed offset for the scrollbar and window frame, which is
 * a larger share of a narrow window than a wide one, so it is snapped to the
 * nearest rung of a zoom ladder rather than trusted directly. A reading outside
 * the ladder's range means the measurement is not trustworthy on this browser,
 * and it falls back to 1 — today's behaviour.
 *
 * The rungs are a union, not one browser's: Safari zooms through 85% and 115%,
 * which Chrome's ladder does not have, and a step with no rung near it snaps to
 * a neighbour several percent away. So the snapped rung is kept off the sizing
 * path entirely — it decides the hold and the damping, where a crisp threshold
 * is worth more than precision, while the width the design is measured against
 * comes from `outerWidth`, which is that same unzoomed width without a guess in
 * it. A ladder this script has never seen then costs nothing.
 *
 * Zoom's effect on text is capped at `CAP`. Past it, `--ap-zoom-damp` shrinks
 * the root by exactly as much as the zoom grew it, so the design keeps a fixed
 * amount of room — `screenWidth / CAP` — at any zoom level beyond the cap
 * rather than an ever-narrowing one. Text still reaches 200% at 200% zoom,
 * which is what SC 1.4.4 asks for, and the cap sits above that so the
 * measurement point is not on the boundary. It buys a bounded worst case: one
 * width has to work rather than every width down to 320px.
 *
 * The desktop branch never meets the cap — it only renders while the design has
 * 1024px of room, which is a scale of 1.875 — so the damping is what the mobile
 * branch reads, and the two carry it differently for the reason below.
 *
 * The tree switches on the raw viewport at 736px, and only ever downgrades:
 * the server already answered from the user agent, and a tablet that asked for
 * the mobile tree keeps it however wide its window is. The override exists to
 * catch a desktop browser with little room, not to promote a phone.
 *
 * It switches on raw viewport rather than on how much room the design has.
 *
 * `--ap-min-width` decides which of two behaviours the desktop layout gets.
 * Below a scale of 1.5 it is absent, so the design shrinks to the viewport as
 * it always has and a little crowding is the price. From 1.5 the design holds
 * the 1920 pixels it was drawn for and the page scrolls sideways instead,
 * because at that point shrinking stops being crowding and starts being
 * collapse — 150% is already down to 1265 of the 1920, with the type at full
 * size in two thirds of the width. The threshold reads the combined scale, so
 * a reader who raised their font size far enough gets the same relief as one
 * who zoomed. Between there and the point the design stops fitting, the
 * desktop layout keeps its `min-width` and scrolls sideways instead of being
 * squeezed — the reader gets the layout they know, at the size they asked for,
 * and reaches the rest of it by scrolling. Below 736px the mobile tree takes
 * over with no minimum, which is what SC 1.4.10 measures: a 1280px window at
 * 400% is 320 CSS px, well inside the mobile range, and nothing scrolls there.
 *
 * It runs inline and render-blocking, ahead of `<Links />`, so the property is
 * set before the stylesheet paints anything: deferring it would size the first
 * frame wrong and then jump. The server cannot supply the value because neither
 * preference leaves the browser, so this stays client-only, and every rule's
 * fallback covers the reader whose script did not run.
 */
const USER_TEXT_SCALE_SCRIPT = `(function(){try{
var root=document.documentElement;
var server=root.getAttribute('data-ap-display');
var p=document.createElement('div');
p.style.cssText='font-size:medium;position:absolute;visibility:hidden';
root.appendChild(p);
var d=parseFloat(getComputedStyle(p).fontSize);
p.remove();
var text=d>0?d/16:1;
var STEPS=[0.25,0.33,0.5,0.67,0.75,0.8,0.85,0.9,1,1.1,1.15,1.25,1.5,1.75,2,2.5,3,4,5];
function zoom(){
var w=window.innerWidth;
if(!w)return 1;
var r=window.outerWidth/w;
if(!(r>0.2&&r<6))return 1;
var best=1,diff=Infinity;
for(var i=0;i<STEPS.length;i++){var x=Math.abs(STEPS[i]-r);if(x<diff){diff=x;best=STEPS[i];}}
return best;
}
var CAP=2.5;
function frame(z){
var w=window.innerWidth,o=window.outerWidth,r=w?o/w:0;
return (r>0.2&&r<6)?o:w*z;
}
var basisW=null,basisPx=0;
function basis(){
var w=window.innerWidth;
if(w===basisW)return basisPx;
var q=document.createElement('div');
q.style.cssText='font-size:min(16px,0.833vw);position:absolute;visibility:hidden';
root.appendChild(q);
var v=parseFloat(getComputedStyle(q).fontSize);
q.remove();
basisW=w;basisPx=v>0?v:0;
return basisPx;
}
var lastScale=null,lastDamp=null,lastHold=null,lastDisplay=null;
function apply(){
var z=zoom();
var capped=Math.min(z,CAP);
var want=text*capped;
var damp=capped/z;
var b=basis();
var target=Math.min(16,0.00833*frame(z))*text;
var scale=b>0?target/b:want;
if(scale!==lastScale){
lastScale=scale;
root.style.setProperty('--ap-text-scale',String(text));
root.style.setProperty('--ap-user-scale',String(scale));
}
if(damp!==lastDamp){
lastDamp=damp;
root.style.setProperty('--ap-zoom-damp',String(damp));
}
var hold=want>=1.5;
if(hold!==lastHold){
lastHold=hold;
if(hold)root.style.setProperty('--ap-min-width','var(--ap-page-width)');
else root.style.removeProperty('--ap-min-width');
}
if(root.getAttribute('data-ap-display')!=='mobile'||lastDisplay!==null){
var display=(server==='mobile'||window.innerWidth<736)?'mobile':'desktop';
if(display!==lastDisplay){
lastDisplay=display;
root.setAttribute('data-ap-display',display);
}
}
}
apply();
window.addEventListener('resize',apply,{passive:true});
(function watchPixelRatio(){
try{
var mq=window.matchMedia('(resolution: '+window.devicePixelRatio+'dppx)');
var onChange=function(){
if(mq.removeEventListener)mq.removeEventListener('change',onChange);
else if(mq.removeListener)mq.removeListener(onChange);
apply();watchPixelRatio();
};
if(mq.addEventListener)mq.addEventListener('change',onChange,{once:true});
else if(mq.addListener)mq.addListener(onChange);
}catch(e){}
})();
}catch(e){}})()`;

/**
 * Full head
 */
const Head = () => {
    const { device } = useRootData();
    const disableMobileUserScaling = useMatchesHandleData<boolean>(
        'disableMobileUserScaling',
        false,
    );

    const noUserScalable =
        device.type !== 'desktop' && disableMobileUserScaling;
    const viewport = noUserScalable
        ? 'width=device-width, initial-scale=1, maximum-scale=1, viewport-fit=cover, user-scalable=no, shrink-to-fit=no'
        : device?.os.isApple
          ? 'width=device-width, initial-scale=1.0, maximum-scale=2, viewport-fit=cover'
          : 'width=device-width,initial-scale=1';

    return (
        <>
            <meta charSet="utf-8" />
            <meta name="viewport" content={viewport} />
            <script
                dangerouslySetInnerHTML={{ __html: USER_TEXT_SCALE_SCRIPT }}
            />
            {/* Progressive enhancement for scroll-reveal content (product grids,
                carousels): it SSRs at opacity-0 and is revealed by a client
                IntersectionObserver. Without JS the observer never fires, so
                force it visible for no-JS agents and crawlers. JS users keep the
                reveal animation untouched. */}
            <noscript>
                <style
                    dangerouslySetInnerHTML={{
                        __html: '.scroll-reveal{opacity:1!important;transform:none!important}',
                    }}
                />
            </noscript>
            {noUserScalable && (
                <meta name="HandheldFriendly" content="true"></meta>
            )}
            <meta name="application-name" content="&nbsp;" />
            <meta name="msapplication-TileColor" content="#FFFFFF" />
            <meta
                name="msapplication-TileImage"
                content="/assets/website/ui/favicon-global-ninjas/favicon-black-144x144.png"
            />
            <meta
                name="msapplication-square70x70logo"
                content="/assets/website/ui/favicon-global-ninjas/favicon-black-70x70.png"
            />
            <meta
                name="msapplication-square150x150logo"
                content="/assets/website/ui/favicon-global-ninjas/favicon-black-150x150.png"
            />
            <meta
                name="msapplication-wide310x150logo"
                content="/assets/website/ui/favicon-global-ninjas/favicon-black-310x150.png"
            />
            <meta
                name="msapplication-square310x310logo"
                content="/assets/website/ui/favicon-global-ninjas/favicon-black-310x310.png"
            />
            <Meta />
            <Links />
        </>
    );
};

/**
 * This is version of the head that `should` not throw errors.
 */
const SafeHead = () => {
    return (
        <>
            <meta charSet="utf-8" />
            <meta
                name="viewport"
                content="width=device-width,initial-scale=1"
            />
            <Links />
        </>
    );
};

const RootPage = () => {
    const { config, localisation, appDisplayMode } = useRootData();
    // Prefer `_app` user: root loader never revalidates, so staff status after
    // SPA login is only visible via the `_app` loader until a full reload.
    const user = useUser();
    const location = useLocation();
    const mockDevice = useMockDevice(location.search);
    const lang = getSiteLang(localisation.country);
    const enableHubspot = !isHubspotStaffUser(user);

    return (
        // `suppressHydrationWarning` because the inline scale script sets
        // `--ap-user-scale` on this element before React hydrates, and React
        // would otherwise report the style attribute as a server/client
        // mismatch and regenerate the tree.
        <html
            lang={lang}
            data-ap-display={appDisplayMode}
            suppressHydrationWarning
        >
            <head suppressHydrationWarning>
                <Head />
                {mockDevice === undefined && (
                    <HeadPixels
                        {...config.pixels}
                        enableHubspot={enableHubspot}
                    />
                )}
            </head>
            <body suppressHydrationWarning>
                <Body>
                    <Outlet />
                </Body>
            </body>
        </html>
    );
};

export const ErrorBoundary = () => {
    const error = useRouteError();
    const rootData = useRouteLoaderData('root') as RootData | undefined;

    const fluentUI = useFluentContext();
    // Detect a missing renderer (root loader failed before the renderer was
    // attached) and re-attach a fresh Fela renderer for the error tree.
    const isNoopProvider = fluentUI.renderer.Provider.name === 'NoopProvider';
    const { current: createFelaRenderer } = useRef(
        createFelaRendererFactory().create,
    );
    const renderWithRenderer = (element: JSX.Element) => {
        if (!isNoopProvider) return element;
        return (
            <RendererProvider value={createFelaRenderer}>
                <Provider theme={baseTheme}>{element}</Provider>
            </RendererProvider>
        );
    };

    let errorObj: Error = new Error('Unknown error');
    if (isRouteErrorResponse(error)) {
        const data = error.data;
        const msg =
            typeof data === 'string'
                ? data
                : ((data as { message?: string } | null)?.message ??
                  `${error.status}`);
        errorObj = new Error(`${error.status}: ${msg}`);
    } else if (error instanceof Error) {
        errorObj = error;
    }

    const lang = rootData?.localisation?.country
        ? getSiteLang(rootData.localisation.country)
        : 'en';
    const errorPage = <ErrorPage error={errorObj} />;
    const bodyContent = rootData
        ? renderWithRenderer(<Body>{errorPage}</Body>)
        : // No root data means `<Body>` (which renders `<Scripts />`) is skipped;
          // include `<Scripts />` explicitly so the client can still hydrate and
          // recover instead of being stuck on a script-less static error page.
          renderWithRenderer(
              <>
                  {errorPage}
                  <Scripts />
              </>,
          );

    return (
        // `suppressHydrationWarning` because the inline scale script sets
        // `--ap-user-scale` on this element before React hydrates, and React
        // would otherwise report the style attribute as a server/client
        // mismatch and regenerate the tree.
        <html
            lang={lang}
            data-ap-display={rootData?.appDisplayMode}
            suppressHydrationWarning
        >
            <head suppressHydrationWarning>
                <SafeHead />
            </head>
            <body suppressHydrationWarning>{bodyContent}</body>
        </html>
    );
};

export default RootPage;
